import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicGet } from '../services/apiService';
import { getFullUrl } from '../config/apiConfig';
import { 
  Calendar, 
  User, 
  Tag, 
  ArrowLeft,
  Share2,
  Clock
} from 'lucide-react';

const BlogPost = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBlog = useCallback(async () => {
    try {
      const url = getFullUrl(`blog/${slug}`);
      
      const response = await publicGet(url);
      
      if (response.status === 404) {
        setError('Blog post not found (404)');
        setLoading(false);
        return;
      }
      
      let blogData;
      
      // Check if response is JSON or HTML
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        // Parse JSON response
        const jsonResponse = await response.json();
        
        // Handle the nested structure: { success: true, data: { blog: {...} } }
        if (jsonResponse.success && jsonResponse.data && jsonResponse.data.blog) {
          blogData = jsonResponse.data.blog;
        } else if (jsonResponse.success && jsonResponse.data) {
          // Handle case where blog data is directly in data
          blogData = jsonResponse.data;
        } else {
          blogData = jsonResponse;
        }
      } else {
        // Parse HTML response - backend returns rendered HTML page
        const htmlContent = await response.text();
        
        // Extract blog data from HTML
        blogData = {
          title: extractTitleFromHTML(htmlContent),
          content: extractContentFromHTML(htmlContent),
          excerpt: extractExcerptFromHTML(htmlContent),
          author: extractAuthorFromHTML(htmlContent),
          createdAt: extractDateFromHTML(htmlContent),
          status: 'published'
        };
       }
      
      // Convert published boolean to status string
      if (blogData && blogData.published !== undefined) {
        blogData.status = blogData.published ? 'published' : 'draft';
      }
      
      if (blogData && blogData.status === 'published') {
        // Ensure content is properly formatted
        if (blogData.content) {
        }
        setBlog(blogData);
      } else {
        setError('Blog post not found or not published');
      }
    } catch (error) {
      console.error('Failed to fetch blog:', error);
      setError(`Failed to load blog post: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // Helper functions to extract data from HTML
  const extractTitleFromHTML = (html) => {
    const titleMatch = html.match(/<title[^>]*>(.*?)<\/title>/i) || 
                       html.match(/<h1[^>]*>(.*?)<\/h1>/i);
    return titleMatch ? titleMatch[1].trim() : 'Untitled Blog';
  };

  const extractContentFromHTML = (html) => {
    // Look for main content area
    const contentMatch = html.match(/<main[^>]*>(.*?)<\/main>/is) ||
                         html.match(/<article[^>]*>(.*?)<\/article>/is) ||
                         html.match(/<div[^>]*class="[^"]*content[^"]*"[^>]*>(.*?)<\/div>/is);
    return contentMatch ? contentMatch[1].trim() : html;
  };

  const extractExcerptFromHTML = (html) => {
    const excerptMatch = html.match(/<meta[^>]*name="description"[^>]*content="([^"]*)"[^>]*>/i) ||
                         html.match(/<meta[^>]*property="og:description"[^>]*content="([^"]*)"[^>]*>/i);
    return excerptMatch ? excerptMatch[1] : '';
  };

  const extractAuthorFromHTML = (html) => {
    const authorMatch = html.match(/<meta[^>]*name="author"[^>]*content="([^"]*)"[^>]*>/i) ||
                        html.match(/<span[^>]*class="[^"]*author[^"]*"[^>]*>(.*?)<\/span>/i);
    return authorMatch ? authorMatch[1] : 'Admin';
  };

  const extractDateFromHTML = (html) => {
    const dateMatch = html.match(/<meta[^>]*property="article:published_time"[^>]*content="([^"]*)"[^>]*>/i) ||
                       html.match(/<time[^>]*>(.*?)<\/time>/i);
    return dateMatch ? dateMatch[1] : new Date().toISOString();
  };

  const updatePageMetadata = useCallback(() => {
    // Update document title
    if (blog) {
      document.title = blog.metaTitle || blog.title;
      
      // Update or create meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = blog.metaDescription || blog.excerpt || '';
      
      // Update Open Graph meta tags
      updateMetaTag('og:title', blog.metaTitle || blog.title);
      updateMetaTag('og:description', blog.metaDescription || blog.excerpt || '');
      updateMetaTag('og:type', 'article');
      updateMetaTag('og:url', window.location.href);
      
      if (blog.featuredImage) {
        updateMetaTag('og:image', blog.featuredImage);
        updateMetaTag('twitter:image', blog.featuredImage);
      }
      
      // Twitter Card meta tags
      updateMetaTag('twitter:card', 'summary_large_image');
      updateMetaTag('twitter:title', blog.metaTitle || blog.title);
      updateMetaTag('twitter:description', blog.metaDescription || blog.excerpt || '');
      
      // Article meta tags
      updateMetaTag('article:published_time', blog.createdAt);
      updateMetaTag('article:author', blog.author);
      if (blog.tags) {
        blog.tags.split(',').forEach(tag => {
          updateMetaTag('article:tag', tag.trim());
        });
      }
    }
  }, [blog]);

  useEffect(() => {
    fetchBlog();
  }, [fetchBlog]);

  useEffect(() => {
    updatePageMetadata();
  }, [updatePageMetadata]);

  const updateMetaTag = (property, content) => {
    if (!content) return;
    
    // Try to find existing tag
    let metaTag = document.querySelector(`meta[property="${property}"]`) || 
                  document.querySelector(`meta[name="${property}"]`);
    
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute(property.includes(':') ? 'property' : 'name', property);
      document.head.appendChild(metaTag);
    }
    
    metaTag.content = content;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const calculateReadingTime = (content) => {
    const plainText = content.replace(/<[^>]*>/g, '');
    const wordsPerMinute = 200;
    const words = plainText.split(/\s+/).length;
    const readingTime = Math.ceil(words / wordsPerMinute);
    return readingTime;
  };

  const shareBlog = (platform) => {
    const url = window.location.href;
    const title = blog?.title;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        // Copy to clipboard
        navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const generateStructuredData = () => {
    if (!blog) return null;
    
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": blog.title,
      "description": blog.metaDescription || blog.excerpt || '',
      "image": blog.featuredImage || '',
      "author": {
        "@type": "Person",
        "name": blog.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "NextGen Exam",
        "logo": {
          "@type": "ImageObject",
          "url": window.location.origin + '/logo/nep-logo.png'
        }
      },
      "datePublished": blog.createdAt,
      "dateModified": blog.updatedAt,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": window.location.href
      }
    };
    
    return JSON.stringify(structuredData);
  };

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex justify-center items-center" style={{
        backgroundImage: 'url(/img/bg-new2.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen relative overflow-hidden" style={{
        backgroundImage: 'url(/img/bg-new2.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-4">Blog Post Not Found</h1>
            <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
            <Link
              to="/blog"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Structured Data for SEO */}
      <script type="application/ld+json">
        {generateStructuredData()}
      </script>
      
      <div className="min-h-screen relative overflow-hidden" style={{
        backgroundImage: 'url(/img/bg-new2.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}>
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center space-x-2 text-sm text-gray-500">
              <Link to="/" className="hover:text-gray-700">Home</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-gray-700">Blog</Link>
              <span>/</span>
              <span className="text-gray-900">{blog.title}</span>
            </nav>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back to Blog */}
          <Link
            to="/blog"
            className="inline-flex items-center text-white hover:text-gray-200 mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>

          <article className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Featured Image */}
            {blog.featuredImage && (
              <div className="aspect-w-16 aspect-h-9">
                <img
                  src={blog.featuredImage}
                  alt={blog.title}
                  className="w-full h-64 md:h-96 object-cover"
                />
              </div>
            )}

            <div className="p-8">
              {/* Header */}
              <header className="mb-8">
                {/* Tags */}
                {blog.tags && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {blog.tags.split(',').map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                )}

                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {blog.title}
                </h1>

                {/* Meta Information */}
                <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{blog.author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(blog.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{calculateReadingTime(blog.content)} min read</span>
                  </div>
                </div>

                {/* Share Buttons */}
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 mr-2">Share:</span>
                  <button
                    onClick={() => shareBlog('twitter')}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Share on Twitter"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => shareBlog('facebook')}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Share on Facebook"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => shareBlog('linkedin')}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Share on LinkedIn"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => shareBlog('copy')}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Copy link"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </header>

              {/* Content */}
              <div className="prose prose-lg max-w-none">
                <div 
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                  className="blog-content"
                  style={{
                    '& ul': { listStyleType: 'disc', paddingLeft: '2rem', marginBottom: '1rem' },
                    '& ol': { listStyleType: 'decimal', paddingLeft: '2rem', marginBottom: '1rem' },
                    '& li': { marginBottom: '0.5rem', paddingLeft: '0.5rem' }
                  }}
                />
                <style dangerouslySetInnerHTML={{
                  __html: `
                    .blog-content ul { list-style-type: disc !important; padding-left: 2rem !important; margin-bottom: 1rem !important; }
                    .blog-content ol { list-style-type: decimal !important; padding-left: 2rem !important; margin-bottom: 1rem !important; }
                    .blog-content li { margin-bottom: 0.5rem !important; padding-left: 0.5rem !important; }
                    .blog-content ul li::marker { color: #1f2937 !important; }
                    .blog-content ol li::marker { color: #1f2937 !important; font-weight: bold !important; }
                  `
                }} />
              </div>

              {/* Footer */}
              <footer className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="text-gray-600 text-sm">
                    Published on {formatDate(blog.createdAt)} by {blog.author}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/signup"
                      className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Create Account
                    </Link>
                    <Link
                      to="/blog"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Blog
                    </Link>
                  </div>
                </div>
              </footer>
            </div>
          </article>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
